import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RefreshCw, TrendingUp, LogOut, FileText } from 'lucide-react';
import { MarketScanDownloadCard } from '../components/DownloadableCard';
import SignalsNavigation from '../components/SignalsNavigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function MarketScanPage({ setIsAuthenticated }) {
  const [signals, setSignals] = useState({ market_scan: null, notes: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState('inactive');
  const [daysRemaining, setDaysRemaining] = useState(null);
  const navigate = useNavigate();

  const fetchSignals = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/signals-app/shared-signals`);
      setSignals(response.data);
    } catch (error) {
      console.error('Error fetching signals:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const token = localStorage.getItem('signals_token');
      const response = await axios.get(`${BACKEND_URL}/api/signals-app/subscription-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data.subscription_status);
      setDaysRemaining(response.data.days_remaining);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  useEffect(() => {
    fetchSignals();
    checkSubscription();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('signals_token');
    localStorage.removeItem('signals_user_email');
    setIsAuthenticated(false);
    navigate('/login');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div>Loading signals...</div>
      </div>
    );
  }

  // Check if subscription is inactive or expired
  if (subscriptionStatus === 'inactive' || subscriptionStatus === 'expired') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div style={{ textAlign: 'center', maxWidth: '500px' }}>
          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: '1rem',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 10px 40px rgba(16, 185, 129, 0.3)'
          }}>
            <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem', fontWeight: '600' }}>
              🚀 EARLY ADOPTERS LAUNCH
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', marginBottom: '0.25rem' }}>
              22 DAYS FREE
            </div>
            <div style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.9)' }}>
              Limited Time Only!
            </div>
          </div>

          <div className="card">
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
              {subscriptionStatus === 'expired' ? 'Subscription Expired' : 'Subscription Required'}
            </h2>
            <p style={{ color: '#94a3b8', marginBottom: '1.5rem' }}>
              {subscriptionStatus === 'expired' 
                ? 'Your subscription has expired. Renew now to continue accessing premium AI-powered signals!'
                : 'Subscribe now to access premium AI-powered trading signals.'}
            </p>
            <button
              className="button button-primary"
              onClick={() => navigate('/subscription')}
              style={{ width: '100%', padding: '1rem', fontSize: '1.1rem' }}
            >
              Start Free Trial
            </button>
            <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '1rem' }}>
              Then just $10/month after trial
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '100px' }}>
      {/* Header */}
      <div style={{ background: '#1e293b', padding: '1rem', marginBottom: '0', boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <TrendingUp size={24} style={{ color: '#10b981' }} />
            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>SavageAISignals</h1>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => navigate('/notes')}
              style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer', color: '#f1f5f9', fontWeight: '500' }}
            >
              <FileText size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
              Notes
            </button>
            <button
              onClick={handleLogout}
              style={{ background: 'transparent', border: '1px solid #334155', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer', color: '#f1f5f9' }}
            >
              <LogOut size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <SignalsNavigation activePage="market-scan" />

      {/* Trial Banner */}
      {subscriptionStatus === 'trial' && (
        <div style={{ 
          background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2) 0%, rgba(245, 158, 11, 0.2) 100%)', 
          borderBottom: '1px solid rgba(251, 191, 36, 0.4)',
          padding: '0.75rem 1rem',
          marginBottom: '1rem'
        }}>
          <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.25rem' }}>🎉</span>
              <span style={{ color: '#fbbf24', fontWeight: '600' }}>
                FREE TRIAL: {daysRemaining !== null ? `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining` : 'Active'}
              </span>
            </div>
            <button
              onClick={() => navigate('/subscription')}
              style={{ 
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                border: 'none', 
                padding: '0.5rem 1rem', 
                borderRadius: '0.375rem', 
                cursor: 'pointer', 
                color: 'white', 
                fontWeight: '600',
                fontSize: '0.875rem'
              }}
            >
              Subscribe - $10/month
            </button>
          </div>
        </div>
      )}

      <div className="container">
        {/* Market Scanner Section */}
        {signals.market_scan && (
          <MarketScanDownloadCard marketScan={signals.market_scan} />
        )}

        {/* Notes Section */}
        {signals.notes && signals.notes.length > 0 && (
          <div className="card">
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>📝 Market Commentary</h2>
            {signals.notes.map((note, index) => (
              <div key={index} style={{ marginBottom: '1rem', padding: '1rem', background: '#0f172a', borderRadius: '0.5rem' }}>
                {note.title && <h3 style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{note.title}</h3>}
                <p style={{ fontSize: '0.875rem', lineHeight: '1.5', marginBottom: '0.5rem' }}>{note.content}</p>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  {new Date(note.shared_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!signals.market_scan && signals.notes.length === 0 && (
          <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
            <p style={{ fontSize: '1.125rem', color: '#94a3b8' }}>No market scan available yet. Check back soon!</p>
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <button
        className="refresh-button"
        onClick={() => fetchSignals(true)}
        disabled={refreshing}
      >
        <RefreshCw size={24} className={refreshing ? 'spinning' : ''} />
      </button>
    </div>
  );
}

export default MarketScanPage;
